<template>
  <div :class="containerClasses">
    <CheckButton
      v-for="item in visibleItems"
      :key="item.str"
      :model-value="selectedTypes.includes(item.str)"
      is-list-item
      :label="item.label"
      :iconName="item.iconName"
      @update:model-value="toggleType(item.str)"
    />
  </div>
  <button
    v-if="showMoreButton"
    class="show-more-button"
    @click.stop="showAllItems"
  >
    {{ t('moreButton') }}
  </button>
</template>

<script setup lang="ts">
  import { computed, type Ref, ref, watch, onMounted } from 'vue';
  import { CheckButton } from '../check-button';

  import type { IExperienceAggregations } from '@/entities/experience/aggregation';
  import { sortAndFilterMovementItems } from '../../lib';
  import ru from '../../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import type {
    IMovementType,
    IMovementTypeGroup,
    MovementItem,
    TMovementType,
    TMovementTypeGroup,
  } from '@/entities/movement-type/types';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';

  interface Props {
    aggregationMovementType: IExperienceAggregations['movementType'];
    movementTypes: {
      movementTypeGroups: Ref<IMovementTypeGroup[]>;
      movementTypes: Ref<IMovementType[]>;
    };
    singleColumn?: boolean;
    isCloseModalTrigger?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    isCloseModalTrigger: true,
  });
  const emit = defineEmits<{
    'update:selectedTypes': [Array<TMovementType | TMovementTypeGroup>];
  }>();

  const MAX_VISIBLE_ITEMS = 8;

  const { t } = useI18n({
    inheritLocale: true,
    messages: { ru },
  });

  const filtersStore = useExcursionFiltersStore();

  const selectedTypes = ref<string[]>([]);
  const showAll = ref(false);

  const list = computed<MovementItem[]>(() =>
    sortAndFilterMovementItems(
      props.aggregationMovementType ?? {},
      props.movementTypes.movementTypeGroups.value,
      props.movementTypes.movementTypes.value,
    ),
  );

  const visibleItems = computed(() => {
    if (showAll.value || list.value.length <= MAX_VISIBLE_ITEMS) {
      return list.value;
    }

    return list.value.slice(0, MAX_VISIBLE_ITEMS);
  });

  const showMoreButton = computed(() => !showAll.value && list.value.length > MAX_VISIBLE_ITEMS);

  const containerClasses = computed(() => ({
    'list-container': true,
    'list-container--single-column': props.singleColumn,
  }));

  const initializeSelectedTypes = () => {
    const initiallySelectedFromList = list.value.filter((item) => item.isSelected).map((item) => item.str);

    const previouslySelectedFromStore = filtersStore.state.movementTypes || [];

    selectedTypes.value = Array.from(new Set([...initiallySelectedFromList, ...previouslySelectedFromStore]));

    if (selectedTypes.value.length > 0 && !filtersStore.state.movementTypes) {
      filtersStore.state.movementTypes = [...selectedTypes.value];
    }

    if (selectedTypes.value.length > 0) {
      emit('update:selectedTypes', selectedTypes.value as Array<TMovementType | TMovementTypeGroup>);
    }
  };

  const toggleType = (str: string) => {
    const index = selectedTypes.value.indexOf(str);
    if (index === -1) {
      selectedTypes.value.push(str);
    } else {
      selectedTypes.value.splice(index, 1);
    }
    emit('update:selectedTypes', selectedTypes.value as Array<TMovementType | TMovementTypeGroup>);
    filtersStore.state.movementTypes = selectedTypes.value.length > 0 ? [...selectedTypes.value] : null;
  };

  const showAllItems = () => {
    showAll.value = true;
  };

  watch(
    () => filtersStore.state.movementTypes,
    (value) => {
      if (value === null) {
        selectedTypes.value = [];
      } else if (Array.isArray(value) && value.length > 0) {
        selectedTypes.value = [...value];
      }
    },
  );

  watch(() => list.value, initializeSelectedTypes, { immediate: true });

  onMounted(initializeSelectedTypes);

  watch(
    () => props.isCloseModalTrigger,
    () => {
      if (!selectedTypes.value.length) {
        showAll.value = false;
      }
    },
  );
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .list-container {
    display: grid;
    grid-template-columns: 1fr;

    @include adaptivity.mf-laptop {
      grid-template-columns: repeat(2, 1fr);
      column-gap: 24px;
    }
  }

  .list-container--single-column {
    grid-template-columns: 1fr;
    width: 100%;
    margin-left: -8px;
    padding: 0;
  }

  .show-more-button {
    width: 100%;
    padding: 12px 16px 12px 0;
    border: none;
    background-color: transparent;
    color: var(--alias-color-link-default);
    text-align: start;
    cursor: pointer;

    @include typography.base-400-400-medium;

    &:hover {
      width: calc(100% + 8px);
      margin-left: -8px;
      padding-left: 8px;
      border-radius: 16px;
      background-color: var(--comp-checkbutton-color-area-hovered, rgba(0, 0, 0, 0.05));
    }
  }
</style>
